import { StyleSheet, View } from "react-native";
import type { TargetZone } from "@heraion/shared";

import type { UiTheme } from "../theme";
import { makeMeterTrackStyle } from "./meterSharedStyles";

// <TargetBar> (CN-target-bar) — THE actual-vs-target meter bar. Lifted out of
// Home/WeeklyTargetsCard when the 2nd callsite landed (AP-3b, the in-workout
// contextual meter), verbatim behaviour, so the Home three-zone card and the
// logger strip render the identical grammar from one place.
//
// Target-vs-actual grammar (the AP track's non-negotiable invariant): the
// ACTUAL is a solid zone-coloured fill; the TARGET is a *different mark* — a
// soft tint band (±band tolerance) plus a down-caret marker at the exact goal,
// never a second solid fill. `over` uses `warn` gold, never `danger` red. Zone
// is reinforced elsewhere by a glyph + word (ZONE_GLYPH / zoneWord below), so
// the bar never carries state on colour alone.
//
// ME-6 — an optional SECOND reference mark, the program's `prescribed` value for
// this muscle, on the SAME scale as the target. It's a DASHED neutral (ink2)
// tick — a different SHAPE from the solid down-caret+stem (no triangle head), so
// a reduced-vision reader tells the two references apart structurally, never by
// colour alone. (The mockup's blue `info` hue is deferred to the theming track;
// ink2 ships the shape now.) Absent/zero prescription → no 2nd mark.

// A glyph paired with the zone word so the state is never colour-only. Exported
// because the value row that sits ABOVE the bar (Home card, in-workout strip)
// reinforces the same zone with the same glyph.
export const ZONE_GLYPH: Record<TargetZone, string> = {
  under: "•",
  "in-band": "✓",
  over: "↑",
};

// AP-9 — a targeted muscle the pinned program never trains (noCoverage). The
// empty-set mark reads as "nothing prescribed here"; its slashed silhouette is
// deliberately UNlike the under dot `•` (a hollow ring blurs into the filled dot
// for a reduced-vision reader), and it pairs with the word below + a louder ink2
// so the state is never carried by colour — or by a near-twin glyph — alone.
export const NO_COVERAGE_GLYPH = "∅";

// The localized "no coverage" word — the meter's fourth state, told apart from
// "building" (under) so a goal the program doesn't feed doesn't masquerade as
// one you just haven't trained yet. Pure (takes the caller's `t`), like zoneWord.
export const noCoverageWord = (
  t: (k: string, opts?: Record<string, unknown>) => string,
): string =>
  t("mobile.home.targetsNoCoverage", { defaultValue: "no coverage" });

// The localized zone word. Pure (takes the caller's `t`) so both callsites
// print the same three states without re-deriving the mapping.
export const zoneWord = (
  t: (k: string, opts?: Record<string, unknown>) => string,
  zone: TargetZone,
): string =>
  zone === "in-band"
    ? t("mobile.home.targetsZoneOnTarget", { defaultValue: "on target" })
    : zone === "over"
      ? t("mobile.home.targetsZoneOver", { defaultValue: "over" })
      : t("mobile.home.targetsZoneUnder", { defaultValue: "building" });

// Zone → text colour for the value-row state word. `in-band` = complete green,
// `over` = warn gold (never danger), `under`/null = muted ink3.
export const zoneTextStyle = (theme: UiTheme, zone: TargetZone) => ({
  color:
    zone === "in-band"
      ? theme.palette.complete
      : zone === "over"
        ? theme.palette.warn
        : theme.palette.ink3,
});

// Zone → solid actual-fill colour. `in-band` = complete green, `over` = warn
// gold (never danger), `under`/null = muted ink3. Exported so a second surface
// that paints the SAME zones (the PG-1 muscle-coverage bodymap figure) reads the
// mapping from here and can never drift from the bar's fill.
export const zoneFillColor = (theme: UiTheme, zone: TargetZone | null): string =>
  zone === "in-band"
    ? theme.palette.complete
    : zone === "over"
      ? theme.palette.warn
      : theme.palette.ink3;

// No target (null) → a quieter neutral fill (the length alone is the datum,
// there's no goal to score against); otherwise the solid zone colour.
const zoneFillStyle = (theme: UiTheme, zone: TargetZone | null) =>
  zone === null
    ? { backgroundColor: theme.palette.ink3, opacity: 0.5 }
    : { backgroundColor: zoneFillColor(theme, zone) };

// Per-bar geometry. Targeted rows scale to their own goal (each target defines
// its own axis, standard for a target meter); untargeted rows scale to the
// caller-supplied `degradedMax` so a set of neutral bars stays comparable.
type BarGeom = {
  hasTarget: boolean;
  fillPct: number;
  bandLeftPct?: number;
  bandWidthPct?: number;
  markerPct?: number;
  // ME-6 — position of the 2nd (prescription) mark, on the same scale as the
  // target. Only set on a scored row that carries a prescription > 0.
  prescribedPct?: number;
};

const pct = (n: number): number => Math.max(0, Math.min(100, n));

// Pure — projects an actual/target pair onto the track. Exported for tests;
// the component is the only render caller. Kept HERE (not re-derived by either
// callsite) so the 12% headroom + scale math live in exactly one place.
export function targetBarGeom(
  actual: number,
  target: number | null,
  bandLow: number | null | undefined,
  bandHigh: number | null | undefined,
  degradedMax: number,
  // AP-9 — a no-coverage row shows no goal marker/band: the program doesn't feed
  // this target, so a precise caret would imply a progression that isn't there.
  // Force the neutral (no-target) track even though the row carries a goal.
  noCoverage = false,
  // ME-6 — the program's prescribed value for this muscle (2nd reference). Null
  // or ≤0 → no 2nd mark. Widens the scale so an over-prescription still lands
  // on the track instead of falling off the right edge.
  prescribed: number | null = null,
): BarGeom {
  // NaN/Infinity first, so a non-finite actual can't sail past `<= 0` into a bad fillPct.
  const a = Number.isFinite(actual) ? actual : 0;
  if (!noCoverage && target != null && bandLow != null && bandHigh != null) {
    // Headroom past the band so an on-target fill and the caret don't crowd
    // the right edge, and an over fill still has room to read as "past it".
    // The prescription mark shares this scale — include it in the max so a
    // prescription above the band doesn't overflow the track (handoff §1).
    const scale = Math.max(a, bandHigh, prescribed ?? 0) * 1.12 || 1;
    return {
      hasTarget: true,
      fillPct: a <= 0 ? 0 : Math.max(3, pct((a / scale) * 100)),
      bandLeftPct: pct((bandLow / scale) * 100),
      bandWidthPct: pct(((bandHigh - bandLow) / scale) * 100),
      markerPct: pct((target / scale) * 100),
      prescribedPct:
        prescribed != null && prescribed > 0
          ? pct((prescribed / scale) * 100)
          : undefined,
    };
  }
  return {
    hasTarget: false,
    fillPct: a <= 0 ? 0 : Math.max(3, pct((a / degradedMax) * 100)),
  };
}

export type TargetBarProps = {
  theme: UiTheme;
  // The recorded actual, in the metric's unit (hard-set count or tonnage kg).
  actual: number;
  // The goal, or null for an untargeted (degraded) row — then the bar is a
  // neutral length scaled to `degradedMax`, no band/marker.
  target: number | null;
  // In-band edges = target × band multipliers. Ignored when target is null.
  bandLow?: number | null;
  bandHigh?: number | null;
  // Which zone `actual` falls in (colours the fill). Null when untargeted.
  zone: TargetZone | null;
  // Scale denominator for the no-target branch (the caller's busiest untargeted
  // row). Defaults to 1 (a lone neutral bar reads full).
  degradedMax?: number;
  // AP-9 — render the inert neutral track (no goal marker/band, muted fill) even
  // when the row carries a goal: the program prescribes ~0 for this target.
  noCoverage?: boolean;
  // ME-6 — the program's prescribed value for this muscle (2nd reference mark).
  // Null/≤0 → no 2nd mark (retro-compatible with every non-ME-6 callsite).
  prescribed?: number | null;
};

export function TargetBar({
  theme,
  actual,
  target,
  bandLow,
  bandHigh,
  zone,
  degradedMax = 1,
  noCoverage = false,
  prescribed = null,
}: TargetBarProps) {
  const styles = makeStyles(theme);
  const g = targetBarGeom(
    actual,
    target,
    bandLow,
    bandHigh,
    degradedMax,
    noCoverage,
    prescribed,
  );
  return (
    // Two layers on purpose: the outer wrapper does NOT clip (the caret marker
    // + stem overflow the track upward), only the inner track clips its fill +
    // band to the rounded corners. Collapsing these would clip the caret.
    <View style={styles.barWrap}>
      <View style={styles.track}>
        {g.hasTarget ? (
          <View
            style={[
              styles.band,
              { left: `${g.bandLeftPct ?? 0}%`, width: `${g.bandWidthPct ?? 0}%` },
            ]}
          />
        ) : null}
        <View
          style={[
            styles.fill,
            zoneFillStyle(theme, noCoverage ? null : zone),
            { width: `${g.fillPct}%` },
          ]}
        />
      </View>
      {/* ME-6 prescription mark first, so the target caret sits on top when the
          two references coincide (the goal is the primary reference). */}
      {g.prescribedPct != null ? (
        <View style={[styles.prescribedMark, { left: `${g.prescribedPct}%` }]} />
      ) : null}
      {g.hasTarget ? (
        <>
          <View style={[styles.stem, { left: `${g.markerPct ?? 0}%` }]} />
          <View style={[styles.marker, { left: `${g.markerPct ?? 0}%` }]} />
        </>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p } = theme;
  return StyleSheet.create({
    // marginTop leaves room for the caret marker, which sits above the track
    // (top-negative). The wrapper does NOT clip; the track clips its own fill.
    barWrap: { marginTop: 14, position: "relative" },
    // Chrome condiviso con LandmarkBar — una sola definizione (meterSharedStyles).
    ...makeMeterTrackStyle(theme),
    // Soft tint band = the target tolerance, sitting behind the fill.
    band: {
      position: "absolute",
      top: 0,
      bottom: 0,
      backgroundColor: p.ink3,
      opacity: 0.16,
    },
    // Down-caret target marker (thermometer/ruler slider). RN triangle via the
    // transparent-borders trick. translateX(-5) centres it on the target x.
    marker: {
      position: "absolute",
      top: -9,
      width: 0,
      height: 0,
      borderLeftWidth: 5,
      borderRightWidth: 5,
      borderTopWidth: 8,
      borderLeftColor: "transparent",
      borderRightColor: "transparent",
      borderTopColor: p.ink,
      transform: [{ translateX: -5 }],
    },
    // A thin stem from the caret down through the track — pins the exact goal.
    stem: {
      position: "absolute",
      top: -3,
      height: 16,
      width: 1.5,
      backgroundColor: p.ink,
      transform: [{ translateX: -0.75 }],
    },
    // ME-6 — the prescription (2nd reference) mark: a DASHED neutral tick that
    // sits low and pokes BELOW the track — the opposite silhouette from the
    // target's caret+stem, which carries a triangle head and pokes ABOVE. The
    // distinction is structural, not tonal: ink vs ink2 is only ~1.9:1, so a
    // reduced-vision reader must tell the marks apart by shape, and a low
    // downward nub can't be confused with a tall up-caret even blurred, or if a
    // platform renders the dash as a solid line. Living in a different vertical
    // zone also means it stays visible when prescribed ≈ target (no occlusion).
    // borderStyle:"dashed" is the established in-repo line-not-box idiom
    // (LineChart swatchDashed).
    prescribedMark: {
      position: "absolute",
      top: 5,
      height: 12,
      width: 0,
      borderLeftWidth: 2,
      borderStyle: "dashed",
      borderLeftColor: p.ink2,
      transform: [{ translateX: -1 }],
    },
  });
}
